import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { Icon } from "@src/appV2/redesign/components/Icon";
import { Text } from "@src/appV2/redesign/components/Text";
import { useDisplayTime } from "@src/appV2/redesign/components/LocalTime/useDisplayTime";

import { getCityFromTimezone } from "./timezone";

/**
 * Passive pill showing the workplace being viewed + its local time on a single
 * line. Renders for any session that has a workplace profile loaded — admins
 * impersonating a workplace, and workplace users themselves.
 */
export function WorkplaceContextChip() {
  const { profile, tz } = useSession();
  const workplaceName = profile?.name;
  const localTime = useDisplayTime({ timeZone: tz });

  if (!isDefined(workplaceName)) {
    return null;
  }

  const city = isDefined(tz) ? getCityFromTimezone(tz) : undefined;
  const showLocalInfo = isDefined(localTime) && isDefined(city);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      sx={(theme) => ({
        paddingLeft: 3,
        paddingRight: 4,
        paddingY: 1,
        borderRadius: theme.borderRadius?.xxLarge,
        backgroundColor: theme.palette.navbar?.itemSelectedBackground,
        // Sets CSS `color`, which `Icon` inherits through `currentColor` on its
        // SVG stroke. (Icon's own `sx` callback isn't honored by the wrapper.)
        color: theme.palette.navbar?.itemContent,
        minWidth: 0,
        maxWidth: "26rem",
      })}
    >
      <Icon type="building" size="small" sx={{ flexShrink: 0 }} />
      <Text
        variant="body2"
        semibold
        noWrap
        sx={(theme) => ({ color: theme.palette.navbar?.userName, minWidth: 0 })}
      >
        {workplaceName}
      </Text>
      {showLocalInfo && (
        <>
          <Text
            variant="caption"
            inline
            sx={(theme) => ({ color: theme.palette.navbar?.sectionDivider, flexShrink: 0 })}
          >
            ·
          </Text>
          <Text
            variant="caption"
            noWrap
            sx={(theme) => ({
              color: theme.palette.navbar?.itemContent,
              flexShrink: 0,
              letterSpacing: "0.02em",
            })}
          >
            {localTime} {city}
          </Text>
        </>
      )}
    </Stack>
  );
}
